import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import {
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'

import { getBackups, createBackup, exportAllConfigs, importConfigs, restoreBackup, deleteBackup } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'

const Backups = () => {
  const queryClient = useQueryClient()

  // 获取备份列表
  const { data: backupsResponse, isLoading, error } = useQuery({
    queryKey: ['backups'],
    queryFn: getBackups,
    refetchInterval: 30000, // 30秒刷新一次
  })

  const backups = backupsResponse?.data || []

  // 创建备份
  const createBackupMutation = useMutation({
    mutationFn: createBackup,
    onSuccess: () => {
      queryClient.invalidateQueries(['backups'])
      toast.success('备份创建成功')
    },
    onError: (error) => {
      toast.error(`创建备份失败: ${error.message}`)
    },
  })

  // 导出配置
  const exportConfigsMutation = useMutation({
    mutationFn: exportAllConfigs,
    onSuccess: (response) => {
      // 创建下载链接
      const url = window.URL.createObjectURL(response)
      const link = document.createElement('a')
      link.href = url

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.download = `homepage-config-${timestamp}.zip`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('配置导出成功')
    },
    onError: (error) => {
      toast.error(`导出失败: ${error.message}`)
    },
  })

  // 导入配置
  const importConfigsMutation = useMutation({
    mutationFn: importConfigs,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['backups'])
      queryClient.invalidateQueries(['config'])
      toast.success(`配置导入成功！导入了 ${response.importedFiles?.length || 0} 个文件`)
    },
    onError: (error) => {
      toast.error(`导入失败: ${error.message}`)
    },
  })

  // 恢复备份
  const restoreBackupMutation = useMutation({
    mutationFn: restoreBackup,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['backups'])
      queryClient.invalidateQueries(['config'])
      toast.success(`备份恢复成功！恢复了 ${response.restoredFiles?.length || 0} 个文件`)
    },
    onError: (error) => {
      toast.error(`恢复失败: ${error.message}`)
    },
  })

  // 删除备份
  const deleteBackupMutation = useMutation({
    mutationFn: deleteBackup,
    onSuccess: () => {
      queryClient.invalidateQueries(['backups'])
      toast.success('备份删除成功')
    },
    onError: (error) => {
      toast.error(`删除失败: ${error.message}`)
    },
  })

  const handleCreateBackup = () => {
    createBackupMutation.mutate()
  }

  const handleExportConfigs = () => {
    exportConfigsMutation.mutate()
  }

  const handleImportConfigs = (event) => {
    console.log('🔄 文件选择事件触发', event)
    const file = event.target.files[0]
    console.log('📁 选择的文件:', file)

    if (file) {
      console.log('📋 文件信息:', {
        name: file.name,
        type: file.type,
        size: file.size
      })

      if (file.type !== 'application/zip' && !file.name.endsWith('.zip')) {
        console.log('❌ 文件类型不正确:', file.type)
        toast.error('请选择 ZIP 格式的配置文件')
        return
      }

      if (window.confirm('导入配置将覆盖当前配置，是否继续？\n建议先创建备份。')) {
        console.log('✅ 开始导入配置文件:', file.name)
        importConfigsMutation.mutate(file)
      } else {
        console.log('❌ 用户取消导入')
      }
    } else {
      console.log('❌ 未选择文件')
    }
    // 清空文件输入
    event.target.value = ''
  }

  const handleRestoreBackup = (backupName) => {
    if (window.confirm(`确定要恢复备份 "${backupName}" 吗？\n这将覆盖当前配置，该备份将成为当前使用的配置。`)) {
      restoreBackupMutation.mutate(backupName)
    }
  }

  const handleDeleteBackup = (backupName) => {
    if (window.confirm(`确定要删除备份 "${backupName}" 吗？\n此操作不可撤销。`)) {
      deleteBackupMutation.mutate(backupName)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date) => {
    return format(new Date(date), 'yyyy年MM月dd日 HH:mm:ss', { locale: zhCN })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-red-400 mb-2">
          加载备份列表失败
        </h3>
        <p className="text-gray-400 mb-4">{error.message}</p>
        <button
          onClick={() => queryClient.invalidateQueries(['backups'])}
          className="btn-primary"
        >
          重试
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">备份管理</h1>
          <p className="mt-2 text-gray-400">
            管理配置文件备份，支持导入导出功能
          </p>
        </div>
        <div className="flex space-x-3">
          {/* 导入配置 */}
          <div className="relative">
            <input
              id="import-file-input"
              type="file"
              accept=".zip"
              onChange={handleImportConfigs}
              className="hidden"
              disabled={importConfigsMutation.isLoading}
            />
            <label
              htmlFor="import-file-input"
              className={`btn-secondary cursor-pointer inline-flex items-center ${
                importConfigsMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {importConfigsMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  导入中...
                </>
              ) : (
                <>
                  <CloudArrowDownIcon className="h-4 w-4 mr-2" />
                  导入配置
                </>
              )}
            </label>
          </div>

          {/* 导出配置 */}
          <button
            onClick={handleExportConfigs}
            disabled={exportConfigsMutation.isLoading}
            className="btn-secondary"
          >
            {exportConfigsMutation.isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                导出中...
              </>
            ) : (
              <>
                <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                导出配置
              </>
            )}
          </button>

          {/* 创建备份 */}
          <button
            onClick={handleCreateBackup}
            disabled={createBackupMutation.isLoading}
            className="btn-primary"
          >
            {createBackupMutation.isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                创建中...
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                创建备份
              </>
            )}
          </button>
        </div>
      </div>

      {/* 备份统计 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <DocumentDuplicateIcon className="h-6 w-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">
                备份总数
              </p>
              <p className="text-2xl font-bold text-white">
                {backups?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-500/10">
              <ClockIcon className="h-6 w-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">
                最新备份
              </p>
              <p className="text-sm font-bold text-white">
                {backups && backups.length > 0
                  ? format(new Date(backups[0].created), 'MM-dd HH:mm')
                  : '无备份'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <CloudArrowDownIcon className="h-6 w-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">
                总大小
              </p>
              <p className="text-sm font-bold text-white">
                {backups
                  ? formatFileSize(backups.reduce((total, backup) => total + (backup.size || 0), 0))
                  : '0 B'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 功能说明 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">导入导出功能</h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium text-white mb-2">📤 导出配置</h4>
              <p className="text-gray-400 text-sm mb-3">
                将当前所有 Homepage 配置文件打包下载，包括：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• services.yaml - 服务配置</li>
                <li>• settings.yaml - 基础设置</li>
                <li>• widgets.yaml - 小部件配置</li>
                <li>• bookmarks.yaml - 书签配置</li>
                <li>• docker.yaml - Docker 配置</li>
                <li>• custom.css/js - 自定义样式</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-medium text-white mb-2">📥 导入配置</h4>
              <p className="text-gray-400 text-sm mb-3">
                从导出的 ZIP 文件恢复配置，支持：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• 自动备份当前配置</li>
                <li>• 智能识别配置文件</li>
                <li>• 覆盖现有配置</li>
                <li>• 保留备份以便恢复</li>
              </ul>
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-400 text-xs">
                  ⚠️ 导入配置会覆盖当前设置，建议先创建备份
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 备份列表 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">备份历史</h3>
        </div>
        <div className="card-content">
          {backups && backups.length > 0 ? (
            <div className="space-y-3">
              {backups.map((backup, index) => (
                <div
                  key={backup.name}
                  className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600"
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-gray-600">
                      <DocumentDuplicateIcon className="h-5 w-5 text-gray-300" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-white font-medium">
                        {backup.name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span>
                          创建时间: {formatDate(backup.created)}
                        </span>
                        {backup.metadata?.lastRestored && (
                          <span>
                            恢复时间: {formatDate(backup.metadata.lastRestored)}
                          </span>
                        )}
                        <span>
                          大小: {formatFileSize(backup.size || 0)}
                        </span>
                        {backup.metadata?.lastRestored && (
                          <span className="badge-success">
                            当前使用
                          </span>
                        )}
                      </div>
                      {/* 显示备份统计信息 */}
                      {backup.metadata && backup.metadata.stats && (
                        <div className="mt-2 text-xs text-gray-500">
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 mr-2">
                            服务: {backup.metadata.stats.services}个
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-500/10 text-green-400 mr-2">
                            小部件: {backup.metadata.stats.widgets}个
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 mr-2">
                            书签: {backup.metadata.stats.bookmarks}个
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">
                            分组: {backup.metadata.stats.totalGroups}个
                          </span>
                        </div>
                      )}
                      {backup.metadata && backup.metadata.description && (
                        <div className="mt-1 text-xs text-gray-500">
                          {backup.metadata.description}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRestoreBackup(backup.name)}
                      disabled={restoreBackupMutation.isLoading}
                      className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                      title="恢复备份"
                    >
                      <CloudArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBackup(backup.name)}
                      disabled={deleteBackupMutation.isLoading}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                      title="删除备份"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">
                暂无备份
              </h3>
              <p className="text-gray-400 mb-4">
                创建你的第一个配置备份
              </p>
              <button
                onClick={handleCreateBackup}
                disabled={createBackupMutation.isLoading}
                className="btn-primary"
              >
                {createBackupMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    创建中...
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                    创建备份
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 备份说明 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">备份说明</h3>
        </div>
        <div className="card-content">
          <div className="space-y-3 text-sm text-gray-400">
            <div>
              <h4 className="font-medium text-white mb-1">自动备份</h4>
              <p>系统会在配置更新时自动创建备份，确保数据安全。</p>
            </div>
            
            <div>
              <h4 className="font-medium text-white mb-1">备份内容</h4>
              <p>备份包含所有配置文件：settings.yaml、services.yaml、widgets.yaml、bookmarks.yaml、docker.yaml、custom.css、custom.js。</p>
            </div>
            
            <div>
              <h4 className="font-medium text-white mb-1">备份保留</h4>
              <p>系统默认保留最近 50 个备份，超过限制的旧备份会被自动清理。</p>
            </div>
            
            <div>
              <h4 className="font-medium text-white mb-1">恢复备份</h4>
              <p>点击恢复按钮可以将配置恢复到指定的备份状态，该备份将标记为"当前"使用的配置。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Backups
